import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Chip,
    TextField,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import {
    getCodexFromLocation,
    useLiveData,
    pickAutoHeat,
    heatHasLiveRunner,
    startSecondsToClientMs,
    formatDuration,
    sexLabel,
    computeCategoryRankings,
} from "../timeserver/uselivedata";

// ⚠️ Page volontairement PUBLIQUE (pas de login/rôle) : la seule barrière
// est un code manuel configuré par course dans Config.jsx (event_config,
// champ "speakerCode"). Ce n'est pas une vraie sécurité — juste un
// filtre léger pour éviter qu'un visiteur au hasard tombe dessus. Si aucun
// code n'est configuré pour la course, la page est accessible directement.
function useSpeakerGate(codex, requiredCode) {
    const storageKey = `speakerAccess:${codex}`;
    const [unlocked, setUnlocked] = useState(
        () => sessionStorage.getItem(storageKey) === "true"
    );

    useEffect(() => {
        // Pas de code configuré pour cette course -> accès direct.
        if (requiredCode === "") setUnlocked(true);
    }, [requiredCode]);

    const tryUnlock = (input) => {
        if (input === requiredCode) {
            sessionStorage.setItem(storageKey, "true");
            setUnlocked(true);
            return true;
        }
        return false;
    };

    return { unlocked, tryUnlock };
}

// 🚦 Un concurrent de la startlist est "au départ, pas encore parti" avec
// exactement la même logique que startlistStatusLabel() dans LiveTiming.jsx :
// pas de temps final, pas de statut DNS/DNF/DSQ, et pas encore en piste.
function isAtStart(s) {
    if (s.final_time != null) return false;
    if (s.status && s.status !== "OK") return false;
    if (s.start_status === "STARTED") return false;
    return true;
}

export default function SpeakerView() {
    const codex = getCodexFromLocation();
    const { data } = useLiveData(codex);
    const heats = useMemo(() => data.heats || {}, [data.heats]);

    const [config, setConfig] = useState(null); // null = pas encore chargé
    useEffect(() => {
        if (!codex) return;
        fetch(`/api/config/${encodeURIComponent(codex)}`)
            .then((r) => (r.ok ? r.json() : {}))
            .then((cfg) => setConfig(cfg || {}))
            .catch(() => setConfig({}));
    }, [codex]);

    const requiredCode = config?.speakerCode || "";
    const { unlocked, tryUnlock } = useSpeakerGate(codex, requiredCode);

    const [codeInput, setCodeInput] = useState("");
    const [codeError, setCodeError] = useState("");

    const [selectedHeat, setSelectedHeat] = useState(null); // null = auto

    const heatKeys = useMemo(
        () => Object.keys(heats).sort((a, b) => Number(a) - Number(b)),
        [heats]
    );
    const effectiveHeat =
        selectedHeat !== null && heats[selectedHeat] ? selectedHeat : pickAutoHeat(heats);
    const heatData = effectiveHeat ? heats[effectiveHeat] : null;
    const competitors = heatData?.competitors || [];
    const startlist = heatData?.startlist || [];

    const categories = config?.categories || [];

    // Classement scratch (rang/écart déjà calculés par listener.py) +
    // classement catégorie (calculé ici, dépend des catégories configurées).
    const rankedFinishers = competitors.filter(
        (c) => c.status === "OK" && c.final_time != null
    );
    const categoryRankings = computeCategoryRankings(rankedFinishers, categories);

    // Ordre d'arrivée réel (chronologique), pas l'ordre de temps : on
    // reconstitue l'heure d'arrivée = heure réelle de départ + temps final.
    const arrivals = rankedFinishers
        .map((c) => {
            const startMs = startSecondsToClientMs(c.start_seconds_since_midnight);
            const arrivalMs = startMs != null ? startMs + c.final_time * 1000 : null;
            const catInfo = categoryRankings[c.bib];
            return { ...c, arrivalMs, catInfo };
        })
        .filter((c) => c.arrivalMs != null)
        .sort((a, b) => b.arrivalMs - a.arrivalMs); // plus récent en premier

    // 🚦 Prochains au départ : concurrents de la startlist encore "au
    // départ" (pas encore partis), dans l'ordre de départ prévu (line).
    const nextStarters = startlist
        .filter(isAtStart)
        .sort((a, b) => (a.line ?? Infinity) - (b.line ?? Infinity))
        .slice(0, 3);

    // --- Barrière d'accès ---
    if (config === null) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Chargement…</Typography>
            </Box>
        );
    }

    if (!unlocked) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
                <Paper sx={{ p: 4, width: 360 }}>
                    <Typography variant="h6" align="center" gutterBottom>
                        Vue speaker — Manche {codex}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                        Saisis le code communiqué par le chronométreur pour accéder à cette vue.
                    </Typography>
                    <TextField
                        fullWidth
                        label="Code speaker"
                        value={codeInput}
                        onChange={(e) => setCodeInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                if (!tryUnlock(codeInput)) setCodeError("Code incorrect");
                            }
                        }}
                        sx={{ mb: 2 }}
                    />
                    {codeError && (
                        <Typography color="error" sx={{ mb: 2 }}>
                            {codeError}
                        </Typography>
                    )}
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => {
                            if (!tryUnlock(codeInput)) setCodeError("Code incorrect");
                        }}
                    >
                        Valider
                    </Button>
                </Paper>
            </Box>
        );
    }

    // --- Vue speaker ---
    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 2 }}>
                Vue speaker
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                {heatKeys.map((key) => (
                    <Chip
                        key={key}
                        label={`Manche ${key}` + (heatHasLiveRunner(heats[key]) ? " ● LIVE" : "")}
                        color={key === effectiveHeat ? "primary" : "default"}
                        onClick={() => setSelectedHeat(key)}
                        sx={{ fontWeight: "bold" }}
                    />
                ))}
                <Chip
                    label="🔴 Auto"
                    color={selectedHeat === null ? "primary" : "default"}
                    onClick={() => setSelectedHeat(null)}
                    sx={{ fontWeight: "bold" }}
                />
            </Box>

            {/* 🚦 Prochains au départ */}
            <Typography variant="h6" sx={{ mb: 1 }}>
                Prochains au départ
            </Typography>

            {nextStarters.length === 0 ? (
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                    Aucun concurrent en attente de départ.
                </Typography>
            ) : (
                <TableContainer component={Paper} sx={{ mb: 3 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ "& th": { fontWeight: "bold" } }}>
                                <TableCell>Ordre</TableCell>
                                <TableCell>Dossard</TableCell>
                                <TableCell>Nom</TableCell>
                                <TableCell>Prénom</TableCell>
                                <TableCell>Sexe</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {nextStarters.map((c, index) => (
                                <TableRow
                                    key={c.bib}
                                    sx={
                                        index === 0
                                            ? { backgroundColor: "#e3f2fd", "& td": { fontWeight: "bold" } }
                                            : undefined
                                    }
                                >
                                    <TableCell>{c.line ?? "—"}</TableCell>
                                    <TableCell>{c.bib}</TableCell>
                                    <TableCell>{c.last_name}</TableCell>
                                    <TableCell>{c.first_name}</TableCell>
                                    <TableCell>{sexLabel(c.sex)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {arrivals.length === 0 ? (
                <Typography>Aucun arrivant pour l'instant.</Typography>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ "& th": { fontWeight: "bold" } }}>
                                <TableCell>Dossard</TableCell>
                                <TableCell>Nom</TableCell>
                                <TableCell>Prénom</TableCell>
                                <TableCell>Sexe</TableCell>
                                <TableCell>Temps final</TableCell>
                                <TableCell>Pos. scratch</TableCell>
                                <TableCell>Écart scratch</TableCell>
                                <TableCell>Catégorie</TableCell>
                                <TableCell>Pos. catégorie</TableCell>
                                <TableCell>Écart catégorie</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {arrivals.map((c, index) => (
                                <TableRow
                                    key={c.bib}
                                    sx={
                                        index === 0
                                            ? { backgroundColor: "#ff9800", "& td": { fontWeight: "bold" } }
                                            : undefined
                                    }
                                >
                                    <TableCell>{c.bib}</TableCell>
                                    <TableCell>{c.last_name}</TableCell>
                                    <TableCell>{c.first_name}</TableCell>
                                    <TableCell>{sexLabel(c.sex)}</TableCell>
                                    <TableCell>{formatDuration(c.final_time)}</TableCell>
                                    <TableCell>{c.rank ?? "—"}</TableCell>
                                    <TableCell>{c.gap != null ? "+" + formatDuration(c.gap) : "—"}</TableCell>
                                    <TableCell>{c.catInfo?.categoryName || "—"}</TableCell>
                                    <TableCell>{c.catInfo?.catRank ?? "—"}</TableCell>
                                    <TableCell>
                                        {c.catInfo?.catGap != null ? "+" + formatDuration(c.catInfo.catGap) : "—"}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}